// === Admin Dashboard ===

import { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Paper,
  IconButton,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Tooltip,
  Button,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Business as BusinessIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  Grass as CropIcon,
  Science as FertIcon,
  Assignment as TaskIcon,
  WaterDrop as IrrigationIcon,
  BugReport as PestIcon,
  Inventory as FertTemplateIcon,
  AccountCircle as UserIcon,
  FiberManualRecord as DotIcon,
  Refresh as RefreshIcon,
  Category as CategoryIcon,
  LocalOffer as PriceIcon,
  Public as TimezoneIcon,
  PinDrop as PostcodeIcon,
  Star as StarIcon,
  PeopleAlt as PeopleAltIcon,
  CalendarMonth as CalendarMonthIcon,
  Insights as InsightsIcon,
  CloudUpload as MigrationsIcon,
  Feedback as FeedbackIcon,
  LinkOff as LinkCheckIcon,
  ChecklistRtl as ChecklistIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import type { Session } from '@supabase/supabase-js';
import { useAuth } from '../lib/auth';
import { useThemeContext } from '../lib/theme-context';
import { alpha } from '@mui/material/styles';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ActiveUser {
  readonly id: string;
  readonly email: string;
  readonly lastActive: string;
}

interface ActiveUsersResponse {
  readonly total: number;
  readonly users: readonly ActiveUser[];
}

// ---------------------------------------------------------------------------
// Quick-nav sections — mirrors the PHP admin route groups
// ---------------------------------------------------------------------------

interface NavSection {
  readonly label: string;
  readonly description: string;
  readonly href: string;
  readonly icon: React.ReactNode;
  readonly color: string;
}

interface NavGroup {
  readonly heading: string;
  readonly items: readonly NavSection[];
}

const NAV_GROUPS: readonly NavGroup[] = [
  {
    heading: 'Advertising / Discount Codes',
    items: [
      { label: 'Advertisings', description: 'Manage advertising banners', href: '/advertisings', icon: <PriceIcon fontSize="large" />, color: '#e65100' },
      { label: 'Discount Codes', description: 'Manage promo discount codes', href: '/discount-codes', icon: <PriceIcon fontSize="large" />, color: '#f57f17' },
    ],
  },
  {
    heading: 'Client Data',
    items: [
      { label: 'Companies', description: 'Manage client companies & subscriptions', href: '/companies', icon: <BusinessIcon fontSize="large" />, color: '#1565c0' },
      { label: 'Client Management', description: 'Client onboarding & account management', href: '/client-management', icon: <BusinessIcon fontSize="large" />, color: '#1976d2' },

    ],
  },
  {
    heading: 'General Settings',
    items: [
      { label: 'Postcodes (AU)', description: 'Australian postcode reference data', href: '/postcodes', icon: <PostcodeIcon fontSize="large" />, color: '#f57c00' },
      { label: 'Pricelist Templates', description: 'Manage subscription pricelist templates', href: '/pricelist-templates', icon: <PriceIcon fontSize="large" />, color: '#00695c' },
      { label: 'Privacy Statements', description: 'Manage privacy statement content', href: '/privacy-statements', icon: <SettingsIcon fontSize="large" />, color: '#455a64' },
      { label: 'States', description: 'Australian states reference data', href: '/states', icon: <TimezoneIcon fontSize="large" />, color: '#00897b' },
      { label: 'Timezones', description: 'Manage supported timezones', href: '/timezones', icon: <TimezoneIcon fontSize="large" />, color: '#546e7a' },
      { label: 'Migration Link', description: 'Developer Only', href: '/database-migrations', icon: <MigrationsIcon fontSize="large" />, color: '#1565c0' },
      { label: 'Bugs / New Features', description: 'Log & track bugs and feature requests', href: '/bug-reports', icon: <FeedbackIcon fontSize="large" />, color: '#d32f2f' },
      { label: 'Chemicals / Fertilizers URL Check', description: 'Run the SDS & label link checker on demand', href: '/document-link-check', icon: <LinkCheckIcon fontSize="large" />, color: '#6d4c41' },
    ],
  },
  {
    heading: 'Program Settings',
    items: [
      { label: 'Categories', description: 'Manage system categories', href: '/categories', icon: <CategoryIcon fontSize="large" />, color: '#4527a0' },
      { label: 'CEC', description: 'Cation exchange capacity formulas', href: '/cecs', icon: <FertIcon fontSize="large" />, color: '#6a1b9a' },
      { label: 'Chemicals', description: 'Chemical product library', href: '/chemicals', icon: <PestIcon fontSize="large" />, color: '#b71c1c' },
      { label: 'Crop Types', description: 'Add & edit crop type definitions', href: '/crop-types', icon: <CropIcon fontSize="large" />, color: '#558b2f' },
      { label: 'Daily Check List', description: 'Manage the daily check list items', href: '/daily-check-list', icon: <ChecklistIcon fontSize="large" />, color: '#00838f' },
      { label: 'Fertilizers', description: 'Fertilizer product library', href: '/fertilizers', icon: <FertTemplateIcon fontSize="large" />, color: '#e65100' },
      { label: 'ML Hours', description: 'ML hours rate configuration', href: '/ml-hours', icon: <IrrigationIcon fontSize="large" />, color: '#0277bd' },
      { label: 'Post Check', description: 'Manage the post check list items', href: '/post-check', icon: <ChecklistIcon fontSize="large" />, color: '#4e342e' },
      { label: 'Post-Harvest Checks', description: 'Post-harvest check templates', href: '/post-harvest-checks', icon: <TaskIcon fontSize="large" />, color: '#ef8c00' },
      { label: 'PPE Equipments', description: 'PPE equipment reference list', href: '/ppe-equipments', icon: <SettingsIcon fontSize="large" />, color: '#00acc1' },
      { label: 'Pre Check', description: 'Manage the pre check list items', href: '/pre-check', icon: <ChecklistIcon fontSize="large" />, color: '#5d4037' },
      { label: 'Pre-Harvest Checks', description: 'Pre-harvest check templates', href: '/pre-harvest-checks-templates', icon: <TaskIcon fontSize="large" />, color: '#558b2f' },
      { label: 'Prog. Settings Lists', description: 'Program settings list config', href: '/program-settings-lists', icon: <SettingsIcon fontSize="large" />, color: '#546e7a' },
      { label: 'Safety Data Sheets', description: 'SDS document management', href: '/safety-data-sheets', icon: <SettingsIcon fontSize="large" />, color: '#d32f2f' },
      { label: 'Surveillance', description: 'Established & exotic pests for surveillance sheets', href: '/surveillance', icon: <PestIcon fontSize="large" />, color: '#c62828' },
      { label: 'SWMS Ranking / Risk', description: 'SWMS risk ranking reference data', href: '/swms-ranking-risk-backends', icon: <TaskIcon fontSize="large" />, color: '#bf360c' },
      { label: 'SWMS Tasks', description: 'SWMS task library', href: '/swms-tasks-backends', icon: <TaskIcon fontSize="large" />, color: '#e64a19' },
      { label: 'Task Types', description: 'Task type definitions', href: '/task-types', icon: <TaskIcon fontSize="large" />, color: '#6a1b9a' },
      { label: 'Titles', description: 'Licence-exempt titles and titles for new companies', href: '/user-titles-backends', icon: <SettingsIcon fontSize="large" />, color: '#4527a0' },
      { label: 'Water Rate Hectares', description: 'Water rate per hectare config', href: '/rate-hectares', icon: <IrrigationIcon fontSize="large" />, color: '#0277bd' },
    ],
  },
  {
    heading: 'Suppliers',
    items: [
      { label: 'Fertilizer Companies', description: 'Manage fertilizer manufacturers', href: '/fertilizer-companies', icon: <FertIcon fontSize="large" />, color: '#e65100' },
      { label: 'Distributors', description: 'Manage chemical/fertilizer distributors', href: '/distributors', icon: <BusinessIcon fontSize="large" />, color: '#1565c0' },
      { label: 'Assign Chemicals', description: 'Assign chemicals to distributors', href: '/chemical-distributor-assignment', icon: <PestIcon fontSize="large" />, color: '#c62828' },
      { label: 'Soil Test Labs', description: 'Soil testing laboratory list', href: '/soil-test-labs', icon: <FertIcon fontSize="large" />, color: '#558b2f' },
    ],
  },
  {
    heading: 'Users',
    items: [
      { label: 'Users', description: 'Manage user accounts & roles', href: '/users', icon: <PeopleIcon fontSize="large" />, color: '#2e7d32' },
    ],
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatRelativeTime(isoString: string): string {
  const diff = Math.floor((Date.now() - new Date(isoString).getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
}

function formatTime(isoString: string): string {
  return new Date(isoString).toLocaleTimeString('en-AU', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ---------------------------------------------------------------------------
// Logged-in users panel
// ---------------------------------------------------------------------------

interface LoggedInPanelProps {
  readonly session: Session | null;
}

function LoggedInPanel({ session }: LoggedInPanelProps) {
  const [data, setData] = useState<ActiveUsersResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);

  const fetchUsers = useCallback(async () => {
    if (!session) return;
    setLoading(true);
    try {
      const apiBase = import.meta.env['VITE_API_URL'] ?? 'http://localhost:8787';
      const res = await fetch(`${apiBase}/api/v1/admin/active-users`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (res.ok) {
        const json = (await res.json()) as ActiveUsersResponse;
        setData(json);
        setLastRefreshed(new Date());
      }
    } catch {
      // Non-fatal — panel keeps stale data
    } finally {
      setLoading(false);
    }
  }, [session]);

  useEffect(() => {
    void fetchUsers();
    const interval = setInterval(() => void fetchUsers(), 5000);
    return () => clearInterval(interval);
  }, [fetchUsers]);

  return (
    <Paper
      elevation={0}
      sx={{
        background: 'linear-gradient(135deg, #4932cd 0%, #3a219e 100%)',
        borderRadius: 2,
        color: '#fff',
        p: 3,
        height: '100%',
        minHeight: 280,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background watermark */}
      <UserIcon
        sx={{
          position: 'absolute',
          bottom: -16,
          right: -16,
          fontSize: 160,
          opacity: 0.08,
          pointerEvents: 'none',
        }}
      />

      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="h3" fontWeight={700} lineHeight={1}>
            {data?.total ?? '—'}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.5 }}>
            Users logged in (last 2 h)
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton
            onClick={() => void fetchUsers()}
            size="small"
            sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff' } }}
          >
            {loading ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <RefreshIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.15)', mb: 2 }} />

      {data && data.users.length > 0 ? (
        <List dense disablePadding sx={{ maxHeight: 260, overflowY: 'auto', pr: 0.5,
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 2 },
        }}>
          {data.users.map((u) => (
            <ListItem key={u.id} disablePadding sx={{ mb: 1 }}>
              <ListItemAvatar sx={{ minWidth: 36 }}>
                <Avatar sx={{ width: 28, height: 28, bgcolor: 'rgba(255,255,255,0.2)', fontSize: 12 }}>
                  {u.email.charAt(0).toUpperCase()}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Typography variant="body2" fontWeight={600} color="#fff" noWrap>
                    {u.email}
                  </Typography>
                }
                secondary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                    <DotIcon sx={{ fontSize: 8, color: '#69f0ae' }} />
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                      {formatRelativeTime(u.lastActive)} · {formatTime(u.lastActive)}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      ) : (
        <Typography
          variant="body2"
          sx={{ opacity: 0.7, fontStyle: 'italic', textAlign: 'center', mt: 3 }}
        >
          No active users right now
        </Typography>
      )}

      {lastRefreshed && (
        <Typography
          variant="caption"
          sx={{ position: 'absolute', bottom: 10, right: 14, opacity: 0.4 }}
        >
          Updated {formatTime(lastRefreshed.toISOString())}
        </Typography>
      )}
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Nav card
// ---------------------------------------------------------------------------

interface NavCardProps {
  readonly section: NavSection;
}

function NavCard({ section }: NavCardProps) {
  const navigate = useNavigate();
  const { gradientCards } = useThemeContext();

  return (
    <Paper
      elevation={0}
      onClick={() => navigate(section.href)}
      sx={{
        p: 3,
        borderRadius: 2,
        cursor: 'pointer',
        border: '1px solid',
        borderColor: `${section.color}40`,
        background: gradientCards
          ? `linear-gradient(135deg, ${section.color}30 0%, ${section.color}0e 100%)`
          : 'unset',
        transition: 'all 0.15s ease',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        '&:hover': {
          borderColor: section.color,
          boxShadow: `0 4px 20px ${section.color}33`,
          transform: 'translateY(-2px)',
          background: gradientCards
            ? `linear-gradient(135deg, ${section.color}45 0%, ${section.color}1a 100%)`
            : `${section.color}10`,
        },
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: 1.5,
          bgcolor: `${section.color}18`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: section.color,
        }}
      >
        {section.icon}
      </Box>
      <Box>
        <Typography variant="subtitle1" fontWeight={700}>
          {section.label}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {section.description}
        </Typography>
      </Box>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const SHORTCUTS_KEY = 'click2gro_admin_shortcuts';

export function AdminDashboardPage() {
  const { session } = useAuth();
  const navigate = useNavigate();

  const [selectedShortcuts, setSelectedShortcuts] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem(SHORTCUTS_KEY);
      return saved ? (JSON.parse(saved) as string[]) : [];
    } catch {
      return [];
    }
  });
  const [shortcutsOpen, setShortcutsOpen] = useState(false);

  const toggleShortcut = (href: string) => {
    setSelectedShortcuts((prev) => {
      const next = prev.includes(href) ? prev.filter((h) => h !== href) : [...prev, href];
      try { localStorage.setItem(SHORTCUTS_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  const allNavItems = NAV_GROUPS.flatMap((g) => g.items);
  const shortcutItems = selectedShortcuts
    .map((href) => allNavItems.find((item) => item.href === href))
    .filter((item): item is NavSection => item !== undefined);

  // ---- Company stats ----
  const [activeCompaniesThisMonth, setActiveCompaniesThisMonth] = useState<number | null>(null);
  const [activeCompaniesThisWeek,  setActiveCompaniesThisWeek]  = useState<number | null>(null);
  const [avgActiveCompaniesYear,   setAvgActiveCompaniesYear]   = useState<number | null>(null);

  // ---- Company stats drill-down dialog ----
  type StatPeriod = 'month' | 'week' | 'year';
  interface StatCompany { company_id: string; company_name: string; last_sign_in: string }
  const [statDialogPeriod, setStatDialogPeriod]   = useState<StatPeriod | null>(null);
  const [statDialogList,   setStatDialogList]     = useState<StatCompany[]>([]);
  const [statDialogLoading, setStatDialogLoading] = useState(false);

  const openStatDialog = (period: StatPeriod) => {
    setStatDialogPeriod(period);
    setStatDialogList([]);
    setStatDialogLoading(true);
    const apiBase = import.meta.env['VITE_API_URL'] ?? 'http://localhost:8787';
    void fetch(`${apiBase}/api/v1/admin/companies/stats/list?period=${period}`, {
      headers: { Authorization: `Bearer ${session?.access_token ?? ''}` },
    })
      .then((r) => r.ok ? r.json() : Promise.reject(r.status))
      .then((body: { data: StatCompany[] }) => { setStatDialogList(body.data); })
      .catch(() => {})
      .finally(() => { setStatDialogLoading(false); });
  };

  useEffect(() => {
    if (!session) return;
    const apiBase = import.meta.env['VITE_API_URL'] ?? 'http://localhost:8787';
    void fetch(`${apiBase}/api/v1/admin/companies/stats`, {
      headers: { Authorization: `Bearer ${session.access_token}` },
    })
      .then((r) => r.ok ? r.json() : Promise.reject(r.status))
      .then((body: { active_companies_this_month: number | null; active_companies_this_week: number | null; avg_active_companies_this_year: number | null }) => {
        setActiveCompaniesThisMonth(body.active_companies_this_month);
        setActiveCompaniesThisWeek(body.active_companies_this_week);
        setAvgActiveCompaniesYear(body.avg_active_companies_this_year);
      })
      .catch(() => { /* non-critical */ });
  }, [session]);

  return (
    <>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Page heading */}
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h4" fontWeight={700}>
              Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {new Date().toLocaleDateString('en-AU', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </Typography>
          </Box>

          {/* Company activity stats */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', ml: 'auto' }}>
            <Box onClick={() => openStatDialog('month')} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1, borderRadius: 2, cursor: 'pointer',
              background: (t) => `linear-gradient(135deg, ${alpha(t.palette.success.main, 0.15)} 0%, ${alpha(t.palette.success.dark, 0.08)} 100%)`,
              border: '1px solid', borderColor: (t) => alpha(t.palette.success.main, 0.35),
              transition: 'all 0.15s', '&:hover': { borderColor: (t) => t.palette.success.main, boxShadow: (t) => `0 0 0 1px ${t.palette.success.main}` } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36,
                borderRadius: '50%', bgcolor: (t) => alpha(t.palette.success.main, 0.22), color: 'success.light', flexShrink: 0 }}>
                <PeopleAltIcon fontSize="small" />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={700} lineHeight={1} color="success.light">
                  {activeCompaniesThisMonth ?? '—'}
                </Typography>
                <Typography variant="caption" color="text.secondary" lineHeight={1.2}>Active Companies This Month</Typography>
              </Box>
            </Box>

            <Box onClick={() => openStatDialog('week')} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1, borderRadius: 2, cursor: 'pointer',
              background: (t) => `linear-gradient(135deg, ${alpha(t.palette.info.main, 0.15)} 0%, ${alpha(t.palette.info.dark, 0.08)} 100%)`,
              border: '1px solid', borderColor: (t) => alpha(t.palette.info.main, 0.35),
              transition: 'all 0.15s', '&:hover': { borderColor: (t) => t.palette.info.main, boxShadow: (t) => `0 0 0 1px ${t.palette.info.main}` } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36,
                borderRadius: '50%', bgcolor: (t) => alpha(t.palette.info.main, 0.22), color: 'info.light', flexShrink: 0 }}>
                <CalendarMonthIcon fontSize="small" />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={700} lineHeight={1} color="info.light">
                  {activeCompaniesThisWeek ?? '—'}
                </Typography>
                <Typography variant="caption" color="text.secondary" lineHeight={1.2}>Active Companies This Week</Typography>
              </Box>
            </Box>

            <Box onClick={() => openStatDialog('year')} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1, borderRadius: 2, cursor: 'pointer',
              background: (t) => `linear-gradient(135deg, ${alpha(t.palette.warning.main, 0.15)} 0%, ${alpha(t.palette.warning.dark, 0.08)} 100%)`,
              border: '1px solid', borderColor: (t) => alpha(t.palette.warning.main, 0.35),
              transition: 'all 0.15s', '&:hover': { borderColor: (t) => t.palette.warning.main, boxShadow: (t) => `0 0 0 1px ${t.palette.warning.main}` } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36,
                borderRadius: '50%', bgcolor: (t) => alpha(t.palette.warning.main, 0.22), color: 'warning.light', flexShrink: 0 }}>
                <InsightsIcon fontSize="small" />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={700} lineHeight={1} color="warning.light">
                  {avgActiveCompaniesYear ?? '—'}
                </Typography>
                <Typography variant="caption" color="text.secondary" lineHeight={1.2} sx={{ whiteSpace: 'nowrap' }}>
                  Avg Active Companies / Month · {new Date().getFullYear()}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Top row: logged-in users + welcome */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={4} lg={3}>
            <LoggedInPanel session={session} />
          </Grid>
          <Grid item xs={12} md={8} lg={9}>
            <Paper
              elevation={0}
              sx={{
                background: 'linear-gradient(135deg, #0e6b71 0%, #08494e 100%)',
                borderRadius: 2,
                color: '#fff',
                p: 3,
                height: '100%',
                minHeight: 280,
                position: 'relative',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: 2,
              }}
            >
              {/* Background watermark */}
              <SettingsIcon
                sx={{
                  position: 'absolute',
                  bottom: -16,
                  right: -16,
                  fontSize: 160,
                  opacity: 0.08,
                  pointerEvents: 'none',
                }}
              />
              <Typography variant="h6" fontWeight={700} color="#fff">
                Welcome back
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.85, maxWidth: 520 }}>
                Manage companies, subscriptions, reference data, templates, and global system
                settings. Use the quick-nav below to jump to any section.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                {/* Shortcut quick-link buttons */}
                {shortcutItems.map((item) => (
                  <Button
                    key={item.href}
                    variant="outlined"
                    size="small"
                    onClick={() => navigate(item.href)}
                    sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)', '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' } }}
                  >
                    {item.label}
                  </Button>
                ))}
                {/* Shortcuts config button */}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<StarIcon fontSize="small" />}
                  onClick={() => setShortcutsOpen(true)}
                  sx={{
                    color: 'rgba(255,255,255,0.75)',
                    borderColor: 'rgba(255,255,255,0.3)',
                    borderStyle: 'dashed',
                    '&:hover': { borderColor: '#fff', color: '#fff', bgcolor: 'rgba(255,255,255,0.08)', borderStyle: 'solid' },
                  }}
                >
                  Shortcuts
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Shortcuts dialog */}
        <Dialog open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <StarIcon color="primary" fontSize="small" />
            Shortcuts
          </DialogTitle>
          <DialogContent dividers sx={{ maxHeight: 460, overflowY: 'auto' }}>
            {NAV_GROUPS.map((group) => (
              <Box key={group.heading} sx={{ mb: 2 }}>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  fontWeight={700}
                  display="block"
                  sx={{ mb: 0.5, borderBottom: '1px solid', borderColor: 'divider', pb: 0.25 }}
                >
                  {group.heading}
                </Typography>
                <FormGroup>
                  {group.items.map((item) => (
                    <FormControlLabel
                      key={item.href}
                      control={
                        <Checkbox
                          size="small"
                          checked={selectedShortcuts.includes(item.href)}
                          onChange={() => toggleShortcut(item.href)}
                        />
                      }
                      label={item.label}
                    />
                  ))}
                </FormGroup>
              </Box>
            ))}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
              {selectedShortcuts.length === 0
                ? 'No shortcuts selected'
                : `${selectedShortcuts.length} shortcut${selectedShortcuts.length === 1 ? '' : 's'} selected`}
            </Typography>
            <Button onClick={() => setShortcutsOpen(false)} variant="contained" size="small">
              Done
            </Button>
          </DialogActions>
        </Dialog>

        {/* Quick nav — grouped */}
        {NAV_GROUPS.map((group) => (
          <Box key={group.heading} sx={{ mt: 5 }}>
            <Typography
              variant="overline"
              color="text.secondary"
              fontWeight={700}
              letterSpacing={1.2}
              sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 0.5, display: 'block', mb: 2 }}
            >
              {group.heading}
            </Typography>
            <Grid container spacing={2}>
              {group.items.map((section) => (
                <Grid item key={section.href} xs={12} sm={6} md={4} lg={3}>
                  <NavCard section={section} />
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}
      </Container>

      {/* Active companies drill-down dialog */}
      <Dialog
        open={statDialogPeriod !== null}
        onClose={() => setStatDialogPeriod(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {statDialogPeriod === 'month' && 'Active Companies This Month'}
          {statDialogPeriod === 'week'  && 'Active Companies This Week'}
          {statDialogPeriod === 'year'  && `Active Companies This Year (${new Date().getFullYear()})`}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {statDialogLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : statDialogList.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
              No companies found for this period.
            </Typography>
          ) : (
            <List dense disablePadding>
              {statDialogList.map((co, i) => (
                <ListItem key={co.company_id} divider={i < statDialogList.length - 1}>
                  <ListItemAvatar>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 13 }}>
                      {co.company_name.charAt(0).toUpperCase()}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={<Typography variant="body2" fontWeight={600}>{co.company_name}</Typography>}
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        Last login: {new Date(co.last_sign_in).toLocaleString('en-AU', {
                          day: '2-digit', month: 'short', year: 'numeric',
                          hour: '2-digit', minute: '2-digit',
                        })}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1, pl: 1 }}>
            {statDialogList.length} {statDialogList.length === 1 ? 'company' : 'companies'}
          </Typography>
          <Button onClick={() => setStatDialogPeriod(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
